import { Suspense, useRef, type ReactNode } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import { useSession } from '@/app/providers/SessionProvider';
import { DawnPatrolAdminLogo } from '@/components/ui/DawnPatrolAdminLogo';
import { DawnPatrolDeveloperLogo } from '@/components/ui/DawnPatrolDeveloperLogo';
import { DawnPatrolLogo } from '@/components/ui/DawnPatrolLogo';
import { useHideOnScroll } from '@/hooks/useHideOnScroll';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { sectionOf } from '@/lib/appSection';
import { MEDIA } from '@/lib/breakpoints';
import { cn } from '@/lib/cn';
import { routeHandle } from '@/lib/routeHandle';

/**
 * Shell shared by every route: skip link, header, main landmark, footer.
 * Pages render into the `Outlet` and own only their own content.
 *
 * Two modes. Page mode (every route by default) is a capped, padded column with
 * a footer. Workspace mode, entered when any matched route has
 * `handle.layout === 'workspace'`, is a full-height app: the header stays, `main`
 * is uncapped and unpadded and fills the rest of the viewport, and there is no
 * footer, so the panes inside can scroll on their own.
 *
 * Below 760px the signed-in header hides on scroll down and comes back on
 * scroll up (UI-12b, `useHideOnScroll`). In workspace mode it slides up by a
 * negative top margin, so `main` (and its panes) grows into the freed height;
 * in page mode it is sticky and slides up over the content with a transform.
 *
 * The background is a dawn horizon built in CSS — the site's hero uses a photo,
 * but hotlinking their CDN would add a third-party request on every load.
 */
interface RootLayoutProps {
    /**
     * Signed-in header content (navigation, account, Sign out). Shown only to a signed-in
     * visitor and never on the consent screen (`/authorize`), which is a
     * partner hand-off and must stay free of app chrome. The layout knows
     * routes and session, never domain data — the caller owns what goes here.
     */
    signedInHeader?: ReactNode;
}

export function RootLayout({ signedInHeader }: Readonly<RootLayoutProps>) {
    const { status } = useSession();
    const { pathname } = useLocation();
    const matches = useMatches();
    const mainRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const showSignedInHeader =
        status === 'signed-in' &&
        signedInHeader !== undefined &&
        !pathname.startsWith('/authorize');
    // The admin app and the developer portal each carry their own brand
    // logo; everything else keeps the original one.
    const section = sectionOf(pathname);
    const isWorkspace = matches.some(
        (match) => routeHandle(match).layout === 'workspace'
    );
    const isWide = useMediaQuery(MEDIA.twoColumns);
    // Below 400px the signed-in bar shows only the logo's wave mark: the same
    // SVG cropped to it, so the link keeps its accessible name.
    const fullLogo = useMediaQuery(MEDIA.fullLogo);
    const compactLogo = showSignedInHeader && !fullLogo;
    // Spread only when compact: an explicit `viewBox={undefined}` would
    // replace the logo's own viewBox and draw the SVG at its natural size.
    // The wave mark is the logos' first 112x112 (all three share it).
    const markProps = compactLogo ? { viewBox: '0 0 112 112' } : {};
    const hidesOnScroll = showSignedInHeader && !isWide;
    const { hidden, height } = useHideOnScroll({
        enabled: hidesOnScroll,
        headerRef,
    });

    return (
        <div
            className={cn(
                'relative flex flex-col',
                // `clip`, not `hidden`, in page mode: it creates no scroll
                // container, so the sticky narrow header still sticks.
                isWorkspace
                    ? 'h-dvh overflow-hidden'
                    : 'min-h-dvh overflow-x-clip'
            )}
        >
            {/* The design system's `bg gradient dark`: a straight vertical
                teal→midnight fade, replacing the radial dawn glow the
                marketing site uses. */}
            <div
                aria-hidden="true"
                className="from-page-top to-page pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b"
            />

            <a
                href="#main"
                // The hash alone does not move focus in every browser (nor in
                // jsdom); focusing `main` makes activation land in the content.
                onClick={() => mainRef.current?.focus()}
                className="bg-accent text-on-accent sr-only rounded-full px-4 py-2 font-semibold focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-10"
            >
                Skip to content
            </a>

            <header
                ref={headerRef}
                className={cn(
                    'border-line shrink-0 border-b',
                    hidesOnScroll &&
                        'z-20 duration-200 motion-reduce:transition-none',
                    hidesOnScroll &&
                        (isWorkspace
                            ? 'transition-[margin-top]'
                            : 'bg-page-top sticky top-0 transition-transform')
                )}
                // The height is measured, so it is a dynamic value.
                style={
                    hidden
                        ? isWorkspace
                            ? { marginTop: -height }
                            : { transform: 'translateY(-100%)' }
                        : undefined
                }
            >
                <div
                    className={cn(
                        showSignedInHeader
                            ? // One row at every width (UI-12b): the logo,
                              // the nav or section picker, the account menu.
                              'cols2:gap-x-6 flex min-h-15 items-center gap-x-3 px-4 py-2'
                            : 'mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-5'
                    )}
                >
                    <a
                        href="https://www.dawnpatrol.cloud"
                        className="text-text-primary shrink-0"
                    >
                        {section === 'admin' && (
                            <DawnPatrolAdminLogo
                                {...markProps}
                                className="h-8 w-auto"
                            />
                        )}
                        {section === 'developer' && (
                            <DawnPatrolDeveloperLogo
                                {...markProps}
                                className="h-8 w-auto"
                            />
                        )}
                        {section === null && (
                            <DawnPatrolLogo
                                {...markProps}
                                className={cn(
                                    'w-auto',
                                    showSignedInHeader ? 'h-7' : 'h-8'
                                )}
                            />
                        )}
                    </a>

                    {showSignedInHeader && (
                        <Suspense fallback={null}>{signedInHeader}</Suspense>
                    )}
                </div>
            </header>

            <main
                id="main"
                ref={mainRef}
                tabIndex={-1}
                className={cn(
                    'flex flex-1',
                    isWorkspace
                        ? 'min-h-0 w-full'
                        : 'mx-auto w-full max-w-5xl px-6'
                )}
            >
                <Outlet />
            </main>

            {!isWorkspace && (
                <footer className="border-line text-text-secondary border-t px-6 py-6 text-center text-sm">
                    © {new Date().getFullYear()} Dawn Patrol Surf Tracking B.V.
                </footer>
            )}
        </div>
    );
}
